import { useEffect, useState, type ReactElement } from "react";
import { IconVideo } from "@tabler/icons-react";
import { Drawer } from "../drawer/Drawer";
import { DrawerInspectorDrillLayoutContext } from "../drawer/DrawerInspectorDrillLayoutContext";
import { CameraLayersCard } from "../inspector/CameraLayersCard";
import {
  INSPECTOR_RAIL_MODULES,
  inspectorModuleBadge,
  renderInspectorModuleBody,
  type InspectorRailsModuleId
} from "../inspector/InspectorRails";
import {
  getInspectorDrawerControls,
  subscribeInspectorDrawerControls
} from "../inspector/inspectorDrawerControlsBridge";
import { getSiteWorkDrawerSource } from "../inspector/siteWorkDrawerBridge";
import {
  RAIL_ICON_SIZE,
  RAIL_ICON_STROKE,
  RailPanelHeader,
  type RailBadge
} from "../rail/IconRail";
import {
  REPORT_RAIL_MODULES,
  ScoreGlyph,
  renderReportModuleBody,
  type ReportRailData,
  type ReportRailModuleId
} from "../report/ReportRail";
import { VIEWER_MOBILE_MEDIA_QUERY } from "../viewerBreakpoint";
import { useMatchMedia } from "../useMatchMedia";
import styles from "./ViewerModuleStrip.module.css";

/** Height of the fixed strip; the sheet's bottom inset clears it. */
export const VIEWER_MODULE_STRIP_HEIGHT_PX = 64;

type StripModule =
  | { kind: "left"; id: InspectorRailsModuleId }
  | { kind: "camera" }
  | { kind: "report"; id: ReportRailModuleId };

function sameModule(a: StripModule | null, b: StripModule): boolean {
  if (a == null) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === "camera") return true;
  return (a as { id: string }).id === (b as { id: string }).id;
}

export interface ViewerModuleStripProps {
  /**
   * Renders the handheld strip inside desktop-width previews (component
   * catalogue). Production `/viewer` never sets this flag.
   */
  cataloguePreview?: boolean;
}

/**
 * Handheld replacement for BOTH desktop rails: one horizontal icon strip
 * pinned to the bottom edge (map tools, a divider, camera, then the report
 * sections — same icons, same order) that opens ONE bottom sheet at a time.
 * The sheet stops at ~55% of the viewport so the map stays visible and layer
 * toggles apply live; there is no staged Apply step.
 */
export function ViewerModuleStrip({
  cataloguePreview = false
}: ViewerModuleStripProps): ReactElement | null {
  const isMobile = useMatchMedia(VIEWER_MOBILE_MEDIA_QUERY);
  const [, bump] = useState(0);
  useEffect(
    () => subscribeInspectorDrawerControls(() => bump((x) => x + 1)),
    []
  );
  const [active, setActive] = useState<StripModule | null>(null);

  useEffect(() => {
    if (!isMobile && !cataloguePreview) setActive(null);
  }, [isMobile, cataloguePreview]);

  if (!isMobile && !cataloguePreview) return null;

  const ctrl = getInspectorDrawerControls();
  const h = ctrl.railHandlers;
  const d = ctrl.details;
  const viewState = ctrl.getViewState();
  const reportToken = h.getReportToken();
  const score = d.getBuildabilityScore();

  const close = (): void => setActive(null);
  const toggle = (m: StripModule): void => {
    setActive((prev) => (sameModule(prev, m) ? null : m));
  };

  const leftModules = INSPECTOR_RAIL_MODULES.filter(
    (m) => m.id !== "illustrations" || reportToken != null
  );

  function stripButton(
    key: string,
    label: string,
    icon: ReactElement,
    module: StripModule,
    badge?: RailBadge
  ): ReactElement {
    const isActive = sameModule(active, module);
    return (
      <button
        key={key}
        type="button"
        className={`${styles.toolButton}${isActive ? ` ${styles.toolButtonActive}` : ""}`}
        aria-label={label}
        aria-pressed={isActive}
        title={label}
        onClick={() => toggle(module)}
      >
        {icon}
        {badge === "dot" ? (
          <span className={styles.badgeDot} aria-hidden="true" />
        ) : typeof badge === "number" && badge > 0 ? (
          <span className={styles.badgeCount} aria-hidden="true">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </button>
    );
  }

  /* ─── Active sheet content ─── */
  let header: ReactElement | null = null;
  let body: ReactElement | null = null;
  if (active != null) {
    if (active.kind === "left") {
      const spec = INSPECTOR_RAIL_MODULES.find((m) => m.id === active.id)!;
      header = (
        <RailPanelHeader
          icon={<spec.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />}
          label={spec.label}
          onClose={close}
        />
      );
      body = renderInspectorModuleBody(
        active.id,
        {
          reportToken,
          illustrationsVersion: h.getIllustrationsVersion(),
          measureActive: undefined,
          onMeasureToggle: undefined,
          onIllustrationsBegin: () => {
            close();
            h.onIllustrationsBegin();
          },
          viewState,
          onBaseVisibilityToggle: h.onBaseVisibilityToggle,
          baseLayerNotes: h.getBaseLayerNotes?.(),
          onTerrainVisibilityToggle: h.onTerrainVisibilityToggle,
          onSunVisibilityToggle: h.onSunVisibilityToggle,
          onSunMonthDayHourChange: h.onSunMonthDayHourChange,
          onSunPreset: h.onSunPreset,
          solar: h.getSolar(),
          onViewsVisibilityToggle: h.onViewsVisibilityToggle,
          onViewsEyeHeightLive: h.onViewsEyeHeightLive,
          onViewsEyeHeightRebuild: h.onViewsEyeHeightRebuild,
          onViewsPickViewpoint: () => {
            close();
            h.onViewsPickViewpoint();
          },
          onViewsResetViewpoint: h.onViewsResetViewpoint,
          onFloodVisibilityToggle: h.onFloodVisibilityToggle,
          onFloodRefreshExternalData: h.onFloodRefreshExternalData,
          utilityInferences: h.getUtilityInferences(),
          onUtilitiesOverlayToggle: h.onUtilitiesOverlayToggle,
          structureBoundsFt: h.getStructureBoundsFt(),
          structuresEditable: h.getStructuresEditable(),
          onStructuresBeginDraw: () => {
            // Dismiss the sheet before draw capture so the canvas is reachable.
            close();
            h.onStructuresBeginDraw();
          },
          onStructuresAutoPlace: h.onStructuresAutoPlace,
          canAutoPlace: h.getCanAutoPlace(),
          getAutoPlacementReasoning: h.getAutoPlacementReasoning,
          onStructureUpdate: h.onStructureUpdate,
          onStructureDelete: h.onStructureDelete,
          onStructureSectionToggle: h.onStructureSectionToggle,
          getEarthworkForStructure: h.getEarthworkForStructure
        },
        "drawer"
      );
    } else if (active.kind === "camera") {
      header = (
        <RailPanelHeader
          icon={<IconVideo size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />}
          label="Camera"
          onClose={close}
        />
      );
      body = (
        <CameraLayersCard
          presentation="drawer"
          onPreset={(preset) => {
            close();
            d.onCameraPreset(preset);
          }}
        />
      );
    } else {
      const spec = REPORT_RAIL_MODULES.find((m) => m.id === active.id)!;
      const siteWork = getSiteWorkDrawerSource();
      const data: ReportRailData = {
        buildabilityScore: score,
        summary: d.getSummary(),
        summaryStatus: d.getSummaryStatus(),
        parcelIntel: d.getParcelIntel(),
        siteCenter: d.getSiteCenter(),
        lot: d.getLot(),
        sunViewsReport: d.getSunViewsReport(),
        externalReport: d.getExternalReport(),
        environmental: d.getEnvironmental(),
        siteWorkEstimate: siteWork.getEstimate(),
        onPrintHandoff: siteWork.onPrintHandoff
      };
      header = (
        <RailPanelHeader
          icon={
            active.id === "propertyReport" ? (
              <ScoreGlyph score={score} />
            ) : (
              <spec.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />
            )
          }
          label={spec.label}
          onClose={close}
        />
      );
      body = renderReportModuleBody(active.id, data, "drawer");
    }
  }

  const navClass = [
    "viewer-bottom-nav",
    styles.strip,
    cataloguePreview && !isMobile ? styles.stripCataloguePreview : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <nav className={navClass} aria-label="Map tools and property report">
        <div className={styles.scroller}>
          {leftModules.map((m) =>
            stripButton(
              m.id,
              m.label,
              <m.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />,
              { kind: "left", id: m.id },
              inspectorModuleBadge(m.id, viewState)
            )
          )}
          <span className={styles.divider} aria-hidden="true" />
          {stripButton(
            "camera",
            "Camera",
            <IconVideo size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />,
            { kind: "camera" }
          )}
          {REPORT_RAIL_MODULES.map((m) =>
            stripButton(
              m.id,
              m.label,
              m.id === "propertyReport" ? (
                <ScoreGlyph score={score} />
              ) : (
                <m.Icon size={RAIL_ICON_SIZE} stroke={RAIL_ICON_STROKE} />
              ),
              { kind: "report", id: m.id }
            )
          )}
        </div>
      </nav>
      <Drawer
        open={active != null}
        onClose={close}
        bottomChromeInsetPx={VIEWER_MODULE_STRIP_HEIGHT_PX}
        sheetHeading="Map panel"
        className={styles.sheet}
        controls={header}
      >
        <DrawerInspectorDrillLayoutContext.Provider value={true}>
          <div className={styles.sheetBody}>{body}</div>
        </DrawerInspectorDrillLayoutContext.Provider>
      </Drawer>
    </>
  );
}
